import type { ReactNode } from "react";

// Card surface — Binance flat color block, no shadow. Use as the default
// content container. `lift` adds a hairline border on hover for affordance
// on link cards (EA rows etc.); skip it for static dashboard panels.
export function Card({
  children,
  className = "",
  lift = false,
}: {
  children: ReactNode;
  className?: string;
  lift?: boolean;
}) {
  return (
    <section
      className={`rounded-xl border border-hairline-dark bg-surface-card p-5 ${
        lift ? "transition hover:border-muted/40" : ""
      } ${className}`}
    >
      {children}
    </section>
  );
}

export function CardTitle({ children, right }: { children: ReactNode; right?: ReactNode }) {
  return (
    <div className="mb-3 flex items-baseline justify-between">
      <h2 className="text-xs font-semibold uppercase tracking-[0.1em] text-muted-strong">
        {children}
      </h2>
      {right ? <div className="text-xs text-muted">{right}</div> : null}
    </div>
  );
}

export function Kpi({
  label,
  value,
  hint,
  tone = "neutral",
  big = false,
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  tone?: "neutral" | "good" | "bad" | "warn" | "primary";
  big?: boolean;
}) {
  const toneClass =
    tone === "good"
      ? "text-trading-up"
      : tone === "bad"
        ? "text-trading-down"
        : tone === "warn"
          ? "text-warn"
          : tone === "primary"
            ? "text-primary"
            : "text-on-dark";
  return (
    <div className="rounded-lg border border-hairline-dark bg-surface-card p-4">
      <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted">{label}</div>
      <div
        className={`mt-1 font-mono font-bold ${big ? "text-3xl" : "text-2xl"} ${toneClass}`}
      >
        {value}
      </div>
      {hint ? <div className="mt-1 text-xs text-muted">{hint}</div> : null}
    </div>
  );
}

export function Pill({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: "neutral" | "good" | "bad" | "warn" | "primary";
}) {
  const cls =
    tone === "good"
      ? "bg-trading-up/15 text-trading-up"
      : tone === "bad"
        ? "bg-trading-down/15 text-trading-down"
        : tone === "warn"
          ? "bg-warn/15 text-warn"
          : tone === "primary"
            ? "bg-primary/20 text-primary"
            : "bg-surface-elevated text-muted-strong";
  return (
    <span
      className={`inline-flex items-center rounded-sm px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${cls}`}
    >
      {children}
    </span>
  );
}

// Inline buy/sell direction badge — text only, no background, used in dense
// tables where colored pills would feel too heavy.
export function DirectionTag({ side }: { side: string }) {
  const upper = side.toUpperCase();
  const isBuy = upper === "BUY";
  const isSell = upper === "SELL";
  return (
    <span
      className={`font-mono text-xs font-semibold ${
        isBuy ? "text-trading-up" : isSell ? "text-trading-down" : "text-muted"
      }`}
    >
      {upper}
    </span>
  );
}
